/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the one canonical animated Vispo mark and product lockup sourced from Marketing Web.
 * @state-owner none
 * @allowed-imports React state and HTML types; Marketing Web authored animated and final-frame assets
 * @forbidden substitute lettermarks; remote logo URLs; module state
 * @public-api all workspace identity, shell, onboarding, loading, and QR surfaces
 * @side-effects animated image rendering only
 * @tests browser visual smoke and BrandMarks.test.tsx
 */

import { type HTMLAttributes, type ImgHTMLAttributes, useEffect, useState } from 'react';

const VISPO_ANIMATED_MARK = new URL(
  '../../../../../apps/marketing/public/logo-intro.webp',
  import.meta.url,
).href;
const VISPO_SETTLED_MARK = new URL(
  '../../../../../apps/marketing/public/logo-mark-static.svg',
  import.meta.url,
).href;
const VISPO_SETTLE_AFTER_MS = 4300;

export function VispoAnimatedMark({ className, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), VISPO_SETTLE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <img
      {...props}
      alt=""
      className={`vispo-animated-mark ${className ?? ''}`}
      data-logo-source="marketing-web-logo-intro"
      data-logo-state={settled ? 'settled' : 'animating'}
      data-testid="vispo-animated-mark"
      decoding="async"
      draggable={false}
      height={128}
      src={settled ? VISPO_SETTLED_MARK : VISPO_ANIMATED_MARK}
      width={128}
    />
  );
}

interface VispoBrandLogoProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  variant?: 'ink' | 'white';
}

export function VispoBrandLogo({
  className,
  size = 32,
  variant = 'ink',
  ...props
}: VispoBrandLogoProps) {
  const markCanvasPx = Math.round(size * 1.34);
  const translateYPx = Math.round(0.32 * markCanvasPx);
  const outerLiftPx = Math.round(translateYPx * 0.75);
  const overlapPx = Math.round(markCanvasPx * 0.2);
  const ink = variant === 'white' ? '#ffffff' : '#0a0a0b';
  const companyInk = variant === 'white' ? 'rgba(255, 255, 255, 0.62)' : '#64748b';

  return (
    <div
      {...props}
      aria-label="Vispo 智加雲"
      className={`vispo-brand-lockup ${className ?? ''}`}
      data-logo-geometry="marketing-original"
      data-logo-source="marketing-web-logo-intro"
      data-logo-variant={variant}
      role="img"
    >
      <span
        className="vispo-brand-lockup__product"
        data-testid="vispo-brand-product"
        style={{ transform: `translateY(-${outerLiftPx}px)` }}
      >
        <VispoAnimatedMark
          style={{
            height: `${markCanvasPx}px`,
            transform: `translateY(${translateYPx}px)`,
            width: `${markCanvasPx}px`,
          }}
        />
        <span
          className="vispo-brand-lockup__name"
          style={{
            fontFamily: "'Hanken Grotesk', -apple-system, Inter, sans-serif",
            fontSize: `${size}px`,
            fontWeight: 600,
            letterSpacing: 0,
            marginLeft: `${-overlapPx}px`,
            color: ink,
          }}
        >
          ispo
        </span>
      </span>
      <span
        className="vispo-brand-lockup__company"
        style={{
          color: companyInk,
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '20px',
        }}
      >
        智加雲
      </span>
    </div>
  );
}
